import React, { useContext, useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polygon, useMap } from "react-leaflet";
import { Box, Typography, Button, Grid, List, ListItem, ListItemText, Paper, TextField, InputAdornment, Snackbar, Alert } from "@mui/material";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import { point, polygon } from "@turf/helpers";
import { AuthContext } from "../../context/AuthContext";
import { getAllDeviceInfo, getGeoFence, getProfileInfo } from "../../services/api";
import SearchIcon from "@mui/icons-material/Search";
import { getDeviceInfo } from "../../services/api";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import RouteIcon from "@mui/icons-material/Route";
import Sidebar from "../../components/Sidebar";
import DirectionsIcon from '@mui/icons-material/Directions';
import { Link, useNavigate } from "react-router-dom";
import Navigation from "./Navigator";
import { Tooltip } from "@mui/material";

// Custom Icons
const insideTruckIcon = new L.Icon({
    iconUrl: "https://www.mexain.com/wp-content/uploads/2025/02/image_2025-02-24_152412764-removebg-preview.png",
    iconSize: [30, 15], iconAnchor: [20, 40], popupAnchor: [0, -40]
});

const outsideTruckIcon = new L.Icon({
    iconUrl: "https://img.icons8.com/emoji/48/000000/red-circle-emoji.png",
    iconSize: [20, 20], iconAnchor: [15, 30], popupAnchor: [0, -30]
});

// Highlighted Device Icon
const selectedTruckIcon = new L.Icon({
    iconUrl: "https://img.icons8.com/emoji/48/000000/green-circle-emoji.png",
    iconSize: [20, 20], iconAnchor: [17, 35], popupAnchor: [0, -35]
});

// Secondary Geofence (Static Boundary)
const SECONDARY_GEOFENCE = [
    { latitude: 13.028651, longitude: 80.276463 },
    { latitude: 13.028340, longitude: 80.277971 },
    { latitude: 13.028227, longitude: 80.279080 },
    { latitude: 13.033067, longitude: 80.279687 },
    { latitude: 13.033443, longitude: 80.277434 },
    { latitude: 13.028651, longitude: 80.276463 }
];

// Status Filter Options
// const STATUS_OPTIONS = ["All", "Work In Progress", "Finished Container", "Empty Container"];
const STATUS_OPTIONS = ["All", "Work In Progress", "Finished Container", "Empty Container", "Outside Geofence"];


// Auto Fit Map to Bounds
const FitToBounds = ({ polygonCoordinates, selectedDevice }) => {
    const map = useMap();
    useEffect(() => {
        if (selectedDevice) {
            map.setView([selectedDevice.latitude, selectedDevice.longitude], 20);
        } else if (polygonCoordinates.length > 0) {
            const bounds = polygonCoordinates.reduce(
                (bounds, { latitude, longitude }) => bounds.extend([latitude, longitude]),
                L.latLngBounds([polygonCoordinates[0].latitude, polygonCoordinates[0].longitude])
            );
            map.fitBounds(bounds, { padding: [50, 50] });
        }
    }, [map, polygonCoordinates, selectedDevice]);
    return null;
};


const GPSMap = () => {
    const { auth } = useContext(AuthContext);
    const [filter, setFilter] = useState("All");
    const [devices, setDevices] = useState([]);
    const [primaryGeofence, setPrimaryGeofence] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState(null);
    const markerRefs = useRef({});
    const sidebarRef = useRef(null);
    const navigate = useNavigate();
    const [username, setUsername] = useState("");

    const routeToNavigator = (device) => {
        console.log("Navigating to directions:", device);

        navigate('/navigator-panel', {
            state: {
                destination: {
                    lat: device.latitude,
                    lng: device.longitude,
                    alt: device.altitude,
                    deviceCode: extractedDeviceCode,
                    batteryPercentage: device.deviceBatteryPercentage,
                    shock: device.shock,
                    speed: device.speed,
                    lastReported: device.receivedAt,
                    name: extractedAtomName,
                }
            }
        });
    };


    useEffect(() => {
        if (auth) {
            fetchAllDeviceInfo();
            fetchGeofence();
            fetchProfile();
        }
        if (auth && sidebarRef.current) {
            sidebarRef.current.toggleDrawer(); //Open sidebar after login
        }
    }, [auth]);

    const fetchProfile = async () => {
        console.log("Calling getProfileInfo...");
        const data = await getProfileInfo(auth);
        if (data?.username) {
            setUsername(data.username);
        }
    };

    useEffect(() => {
        if (devices.length > 0) {
            const anyOutside = devices.some(device => !isInsideGeofence(device));
            if (anyOutside) {
                setShowOutsideTooltip(true);

                // Auto-close tooltip after 5 seconds
                setTimeout(() => setShowOutsideTooltip(false), 5000);
            }
        }
    }, [devices]);

    // Fetch device information from API
    const fetchAllDeviceInfo = async () => {
        const data = await getAllDeviceInfo(auth);
        console.log("Fetched Devices:", JSON.stringify(data, null, 2)); // Debugging

        if (data) {
            setDevices(data.map(device => {
                let shipmentStatus = "Empty Container"; // Default status
                let shipmentId = null; // Default shipmentId

                const shipments = device.shipments;
                if (shipments.length > 0) {
                    const lastShipment = shipments[shipments.length - 1]; // Get the last item in the array
                    shipmentId = lastShipment.id;

                    // Normalize shipment status
                    if (lastShipment.shipmentStatus === "STARTED") {
                        shipmentStatus = "Work In Progress";
                    } else if (lastShipment.shipmentStatus === "CLOSED") {
                        shipmentStatus = "Finished Container";
                    } else {
                        shipmentStatus = lastShipment.shipmentStatus;
                    }
                }

                return {
                    id: device.deviceId,
                    deviceCode: device.deviceCode,
                    name: device.clientName || `Device ${device.deviceId}`,
                    latitude: device.latitude || null,
                    longitude: device.longitude || null,
                    shipmentStatus: shipmentStatus,
                    shipmentId: shipmentId,
                    batteryPercentage: device.batteryPercentage !== undefined ? device.batteryPercentage : "N/A",
                    lastReported: device.lastReported,
                    altitude: device.altitude,
                    speed: device.speed,
                    shock: device.shock,
                    raw: device.raw,
                };
            }));
        }

    };



    // Fetch primary geofence from API
    const fetchGeofence = async () => {
        const data = await getGeoFence(auth);
        if (data && data.boundaryLatLngs) {
            setPrimaryGeofence(data.boundaryLatLngs);
        }
    };

    // Function to check if a device is inside either geofence
    const isInsideGeofence = (device) => {
        if (!device.latitude || !device.longitude) return false;

        const devicePoint = point([device.longitude, device.latitude]);

        // Ensure primaryGeofence has at least 4 points
        if (primaryGeofence.length < 4) {
            console.warn("Primary geofence has less than 4 points, skipping validation.");
            return false;
        }

        // Ensure secondary geofence has at least 4 points
        if (SECONDARY_GEOFENCE.length < 4) {
            console.warn("Secondary geofence has less than 4 points, skipping validation.");
            return false;
        }

        const primaryPolygon = polygon([[
            ...primaryGeofence.map(({ latitude, longitude }) => [longitude, latitude]),
            [primaryGeofence[0].longitude, primaryGeofence[0].latitude] // Ensure closure
        ]]);

        const secondaryPolygon = polygon([[
            ...SECONDARY_GEOFENCE.map(({ latitude, longitude }) => [longitude, latitude]),
            [SECONDARY_GEOFENCE[0].longitude, SECONDARY_GEOFENCE[0].latitude] // Ensure closure
        ]]);

        return booleanPointInPolygon(devicePoint, primaryPolygon) || booleanPointInPolygon(devicePoint, secondaryPolygon);
    };


    const [searchTerm, setSearchTerm] = useState("");

    // Filter Devices Based on Shipment Status
    const statusFilteredDevicesOld = filter === "All"
        ? devices
        : devices.filter(device => device.shipmentStatus === filter);

    const statusFilteredDevices = filter === "All"
        ? devices
        : filter === "Outside Geofence"
            ? devices.filter((device) => !isInsideGeofence(device))
            : devices.filter((device) => device.shipmentStatus === filter);


    // Apply Search Filter on Top of the Status Filter
    const searchFilteredDevices = statusFilteredDevices.filter((device) =>
        device.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        device.deviceCode.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");

    const [deviceDetails, setDeviceDetails] = useState(null);

    // Handle device selection and fetch device info using shipmentId
    const handleDeviceClick = async (device) => {
        if (!device.latitude || !device.longitude || isNaN(device.latitude) || isNaN(device.longitude)) {
            console.warn(`Location not available for ${device.deviceCode}`);
            setSnackbarMessage(`Location not available for DeviceCode: ${device.deviceCode}`);
            setOpenSnackbar(true);
            return;
        }

        // Close any currently open popups
        Object.values(markerRefs.current).forEach((marker) => marker?.closePopup());

        setSelectedDevice(device);
        let updatedDetails = null;

        if (device.shipmentId) {
            try {
                console.log("Fetching device info for shipmentId:", device.shipmentId);

                const lastDeviceInfo = await getDeviceInfo(auth, device.shipmentId);
                console.log("Last Fetched Device Info:", lastDeviceInfo);

                if (lastDeviceInfo) {
                    updatedDetails = {
                        deviceBatteryPercentage: lastDeviceInfo.deviceBatteryPercentage || "N/A",
                        shock: lastDeviceInfo.shock || "N/A",
                        speed: lastDeviceInfo.speed || "N/A",
                        altitude: lastDeviceInfo.altitude || "N/A",
                        latitude: lastDeviceInfo.latitude,
                        longitude: lastDeviceInfo.longitude,
                        receivedAt: lastDeviceInfo.receivedAt ? new Date(lastDeviceInfo.receivedAt).toUTCString() : "N/A",
                        raw: lastDeviceInfo.raw || "N/A",
                    };
                } else {
                    console.warn("No last device info found.");
                }

            } catch (error) {
                console.error("Error fetching device details:", error);
            }
        }

        // Delay showing the popup slightly
        setTimeout(() => {
            if (markerRefs.current[device.id]) {
                markerRefs.current[device.id].openPopup();
            }

            setDeviceDetails(updatedDetails || {
                deviceBatteryPercentage: device.batteryPercentage || "N/A",
                shock: device.shock || "N/A",
                speed: device.speed || "N/A",
                altitude: device.altitude || "N/A",
                latitude: device.latitude,
                longitude: device.longitude,
                receivedAt: device.lastReported ? new Date(device.lastReported).toUTCString() : "N/A",
                raw: device.raw || "N/A",
            });
        }, 100);
    };

    const getValidData = (primary, fallback) => {
        return primary !== 0 && primary !== null && primary !== undefined ? primary :
            fallback !== 0 && fallback !== null && fallback !== undefined ? fallback :
                "N/A";
    };

    const formatData = (value, suffix = "") => {
        return value === "N/A" ? value : `${value} ${suffix}`;
    };

    // Function to extract specific fields from rawAtomString
    const extractFromRawData = (rawAtomString, index) => {
        if (!rawAtomString) return "N/A";

        const extraction = rawAtomString.split(',');
        return extraction[index] ?? "N/A";
    }

    // Usage examples:
    const extractedAtomName = extractFromRawData(deviceDetails?.raw, 3);
    const extractedDeviceCode = extractFromRawData(deviceDetails?.raw, 1);

    const [showOutsideTooltip, setShowOutsideTooltip] = useState(false);

    const activeDevices = statusFilteredDevices.filter((device) => {
        const reportedAt = new Date(device.lastReported);
        const now = new Date();
        const diffInMinutes = (now - reportedAt) / (1000 * 60);
        return !isNaN(reportedAt) && diffInMinutes <= 31;
    });

    const [hideNoDevicesOverlay, setHideNoDevicesOverlay] = useState(false);

    const displayedDevicesOnly = activeDevices.length === 0 && hideNoDevicesOverlay
        ? statusFilteredDevices
        : activeDevices;

    const displayedDevices = React.useMemo(() => {
        const isSelectedDeviceInactive = selectedDevice &&
            !activeDevices.some((device) => device.id === selectedDevice.id);

        // Case 1: No active devices, and no selection → show nothing
        if (activeDevices.length === 0 && !selectedDevice) {
            return [];
        }

        // Case 2: No active devices, overlay is closed → show only selected device
        if (activeDevices.length === 0 && selectedDevice) {
            return [selectedDevice];
        }

        // Case 3: Active devices exist, and selected device is inactive → add it
        if (isSelectedDeviceInactive) {
            return [...activeDevices, selectedDevice];
        }

        // Case 4: Just show active devices
        return activeDevices;
    }, [activeDevices, selectedDevice]);


    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 3, ml: 2 }}>📍 GPS Devices Live Locations</Typography>

            {/* Status Filter */}
            {/* const STATUS_OPTIONS = ["All", "Work In Progress", "Finished Container", "Empty Container"]; */}

            <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 3 }}>
                {/* {STATUS_OPTIONS.map((status) => {
                    // Correctly map "Work In Progress" and "Finished Container" to their updated shipment statuses
                    const count = status === "All"
                        ? devices.length
                        : devices.filter(device => device.shipmentStatus === status).length;

                    return (
                        <Button
                            key={status}
                            variant={filter === status ? "contained" : "outlined"}
                            color={filter === status ? "primary" : "secondary"}
                            onClick={() => setFilter(status)}
                        >
                            {`${status} (${count})`}
                        </Button>
                    );
                })} */}
                {STATUS_OPTIONS.map((status) => {
                    const count = status === "All"
                        ? devices.length
                        : status === "Outside Geofence"
                            ? devices.filter(device => !isInsideGeofence(device)).length
                            : devices.filter(device => device.shipmentStatus === status).length;

                    const isOutsideFilter = status === "Outside Geofence";

                    const button = (
                        <Button
                            key={status}
                            variant={filter === status ? "contained" : "outlined"}
                            color={filter === status ? "primary" : "secondary"}
                            onClick={() => setFilter(status)}
                        >
                            {`${status} (${count})`}
                        </Button>
                    );

                    return isOutsideFilter ? (
                        <Tooltip
                            key={status}
                            title="Devices are away from Boundary. Click here to view."
                            open={showOutsideTooltip}
                            arrow
                        >
                            <span>{button}</span>
                        </Tooltip>
                    ) : (
                        <span key={status}>{button}</span>
                    );
                })}

            </Box>


            <Grid container spacing={2}>
                {/* Device List */}
                <Grid item xs={12} sm={2}>
                    <Paper sx={{ height: "80vh", display: "flex", flexDirection: "column", p: 2 }}>
                        <Typography variant="h6">📋 Device List</Typography>

                        {/* Search Bar (Fixed Position) */}
                        <Box sx={{ position: "sticky", top: 0, backgroundColor: "white", zIndex: 10, pb: 1 }}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                placeholder="Search devices..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                sx={{ mb: 2 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>

                        {/* Scrollable Device List */}
                        <Box sx={{ flex: 1, overflowY: "auto" }}>
                            {/* Check if searchFilteredDevices is null or empty */}
                            {(!searchFilteredDevices || searchFilteredDevices.length === 0) ? (
                                <Box sx={{ padding: 2, textAlign: "center", color: "gray" }}>
                                    No devices available
                                </Box>
                            ) : (
                                <List>
                                    {searchFilteredDevices.map((device) => (
                                        <ListItem
                                            button
                                            key={device.shipmentId}
                                            onClick={() => {
                                                handleDeviceClick(device);
                                                // getDeviceInfo(auth, device.shipmentId);
                                            }}
                                            sx={{
                                                backgroundColor: selectedDevice?.id === device.id ? "rgba(25, 118, 210, 0.1)" : "transparent",
                                                borderLeft: selectedDevice?.id === device.id ? "4px solid #1976d2" : "none",
                                                cursor: "grab",
                                                "&:hover": { backgroundColor: "rgba(25, 118, 210, 0.05)" },
                                            }}
                                        >
                                            <ListItemText primary={device.name} secondary={`📡 ${device.deviceCode}`} />

                                        </ListItem>
                                    ))}

                                </List>
                            )}
                        </Box>

                    </Paper>
                </Grid>

                {/* Map Section */}
                <Grid item xs={12} sm={10}>
                    <Box sx={{ width: "100%", height: "80vh", position: 'relative' }}> {/* Responsive container for the map */}
                        <MapContainer
                            center={[12.881276, 79.911454]}
                            zoom={18} maxZoom={30}
                            style={{ height: '100%', width: '100%' }} // Ensures full width & height
                        >
                            <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png" />
                            {/* <FitToBounds polygonCoordinates={[...primaryGeofence, ...SECONDARY_GEOFENCE]} selectedDevice={selectedDevice} /> */}
                            <FitToBounds polygonCoordinates={[...primaryGeofence]} selectedDevice={selectedDevice} />

                            {/* Primary & Secondary Geofence */}
                            <Polygon positions={primaryGeofence.map(({ latitude, longitude }) => [latitude, longitude])} pathOptions={{ fillColor: "lightyellow", fillOpacity: 0.3, border: 1, color: "red", weight: 1, }} />
                            <Polygon positions={SECONDARY_GEOFENCE.map(({ latitude, longitude }) => [latitude, longitude])} pathOptions={{ color: "blue", fillOpacity: 0.1 }} />

                            {/* Device Markers */}
                            {displayedDevices.map((device) => {

                                if (!device.latitude || !device.longitude || isNaN(device.latitude) || isNaN(device.longitude)) {
                                    console.warn(`Skipping device: ${device.name || "Unknown"} due to invalid coordinates - (${device.latitude}, ${device.longitude})`);
                                    return null;
                                }

                                const inside = isInsideGeofence(device);
                                const lastReportedDate = new Date(device.lastReported);
                                const formattedDate = lastReportedDate.toString();
                                return (
                                    <Marker
                                        key={device.id}
                                        position={[device.latitude, device.longitude]}
                                        icon={selectedDevice?.id === device.id ? selectedTruckIcon : inside ? insideTruckIcon : outsideTruckIcon}
                                        ref={(marker) => (markerRefs.current[device.id] = marker)} // Store marker ref
                                        eventHandlers={{
                                            click: () => handleDeviceClick(device) // Fetch data on marker click
                                        }}
                                    >
                                        <Popup>
                                            <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#1976d2" }}>
                                                📡 {device.name}
                                            </Typography>

                                            <Grid container spacing={1} alignItems="center">
                                                <Grid item xs={12}>
                                                    <Typography variant="caption" sx={{ fontWeight: "bold", display: "flex", alignItems: "center" }}>
                                                        <RouteIcon fontSize="small" sx={{ mr: 0.5 }} /> Device Code: {device.deviceCode}
                                                    </Typography>
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Typography variant="caption" sx={{ color: inside ? "green" : "red", display: "flex", alignItems: "center" }}>
                                                        <AssignmentTurnedInIcon fontSize="small" sx={{ mr: 0.5 }} />
                                                        {inside ? "Inside Geofence ✅" : "Outside Geofence ❌"}
                                                    </Typography>
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Typography variant="caption" sx={{ display: "flex", alignItems: "center" }}>
                                                        <BatteryFullIcon fontSize="small" sx={{ mr: 0.5 }} />
                                                        Battery: {formatData(getValidData(deviceDetails?.deviceBatteryPercentage, device.batteryPercentage), "%")}
                                                    </Typography>
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Typography variant="caption" sx={{ display: "flex", alignItems: "center" }}>
                                                        ⚡ Shock: {getValidData(deviceDetails?.shock, device.shock)}
                                                    </Typography>
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Typography variant="caption" sx={{ display: "flex", alignItems: "center" }}>
                                                        🚀 Speed: {formatData(getValidData(deviceDetails?.speed, device.speed), "km/h")}
                                                    </Typography>
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Typography variant="caption" sx={{ display: "flex", alignItems: "center" }}>
                                                        ⛰️ Altitude: {formatData(getValidData(deviceDetails?.altitude, device.altitude), "m")}
                                                    </Typography>
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Typography variant="caption" sx={{ display: "flex", alignItems: "center" }}>
                                                        <LocationOnIcon fontSize="small" sx={{ mr: 0.5 }} />
                                                        {getValidData(deviceDetails?.latitude?.toFixed(5), device.latitude?.toFixed(5)) !== "N/A" &&
                                                            `${getValidData(deviceDetails?.latitude?.toFixed(5), device.latitude?.toFixed(5))}, 
                                                        ${getValidData(deviceDetails?.longitude?.toFixed(5), device.longitude?.toFixed(5))}`
                                                        }
                                                    </Typography>
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Typography variant="caption">
                                                        Reported: {deviceDetails?.receivedAt && !isNaN(new Date(deviceDetails.receivedAt))
                                                            ? new Date(deviceDetails.receivedAt).toLocaleString("en-IN", {
                                                                timeZone: "Asia/Kolkata", // Convert UTC to IST
                                                                year: "numeric",
                                                                month: "2-digit",
                                                                day: "2-digit",
                                                                hour: "2-digit",
                                                                minute: "2-digit",
                                                                second: "2-digit",
                                                                hour12: true
                                                            })
                                                            : "N/A"}
                                                    </Typography>

                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={<DirectionsIcon fontSize="medium" />}
                                                        onClick={() => routeToNavigator(deviceDetails)}
                                                    >
                                                        Direction
                                                    </Button>

                                                </Grid>
                                            </Grid>
                                        </Popup>
                                    </Marker>
                                );
                            })}

                        </MapContainer>
                        {activeDevices.length === 0 && !selectedDevice && !hideNoDevicesOverlay && (

                            <div
                                style={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    background: "rgba(255, 255, 255, 0.95)",
                                    padding: "1.5rem 2.5rem",
                                    borderRadius: "8px",
                                    boxShadow: "0 0 10px rgba(0,0,0,0.2)",
                                    zIndex: 1000,
                                    fontWeight: "bold",
                                    fontSize: "1.2rem",
                                    color: "#555",
                                    textAlign: "center",
                                    minWidth: "300px",
                                }}
                            >
                                <div
                                    style={{
                                        position: "absolute",
                                        top: 8,
                                        right: 12,
                                        cursor: "pointer",
                                        fontSize: "1.2rem",
                                        fontWeight: "bold",
                                        color: "#888",
                                    }}
                                    onClick={() => setHideNoDevicesOverlay(true)}
                                    title="Close"
                                >
                                    &times;
                                </div>
                                <h4>🚫 No active devices reported in the last 30 minutes</h4>
                                <pre style={{ fontSize: "14px" }}>To see last status close this dialog</pre>
                            </div>
                        )}
                    </Box>
                </Grid>
            </Grid>
            <Snackbar
                open={openSnackbar}
                autoHideDuration={3000}
                onClose={() => setOpenSnackbar(false)}
                anchorOrigin={{ vertical: "top", horizontal: "right" }} // Show in Top-Right
            >
                <Alert onClose={() => setOpenSnackbar(false)} severity="warning" variant="filled">
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>

    );
}
export default GPSMap;
